import React from 'react';
import { Img } from 'remotion';
import { AUTOCLIPPER_LOGO_DATA_URL } from '@/assets/watermark/autoclipperLogoDataUrl';
import { TRACE_PERIMETER, TRACE_RECT, type WatermarkFrameState } from '@/lib/watermark/watermarkTimeline';
import { WATERMARK_DESIGN_HEIGHT, WATERMARK_DESIGN_WIDTH } from '@/lib/watermark/watermarkPosition';
import { WATERMARK_FONT_FAMILY } from './useWatermarkFont';

const CANVAS = '#080d05';
const INK = '#f6faf3';
const LIME = '#a1f400';
const LIME_BRIGHT = '#b5ff00';
const MINT = '#2bffa1';
const LINE_H = 68;

/** One word/icon in its own clipping mask; `y` is the yPercent offset. */
function Item({ y, children }: { y: number; children: React.ReactNode }) {
  return (
    <span style={{ display: 'block', overflow: 'hidden', height: LINE_H, flex: 'none' }}>
      <span style={{ display: 'flex', alignItems: 'center', height: LINE_H, lineHeight: `${LINE_H}px`, transform: `translateY(${y}%)` }}>
        {children}
      </span>
    </span>
  );
}

function Chip({ text, scale = 1 }: { text: string; scale?: number }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 60,
        padding: '0 16px',
        borderRadius: 999,
        background: CANVAS,
        fontWeight: 800,
        transform: scale === 1 ? undefined : `scale(${scale})`,
      }}
    >
      <span
        style={{
          display: 'block',
          lineHeight: '60px',
          background: `linear-gradient(90deg, ${LIME_BRIGHT}, ${MINT})`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {text}
      </span>
    </span>
  );
}

const ICON = { width: 46, height: 46, viewBox: '0 0 24 24', style: { display: 'block', overflow: 'visible' } } as const;

function SparklesIcon({ s }: { s: WatermarkFrameState['sparkle'] }) {
  return (
    <svg {...ICON} aria-hidden>
      <g transform={`translate(12 12) rotate(${s.mainRotation}) scale(${s.mainScale}) translate(-12 -12)`}>
        <path
          d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
          fill={CANVAS}
          stroke={CANVAS}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      </g>
      <g transform={`translate(20 5) scale(${s.smallScaleA}) translate(-20 -5)`}>
        <path d="M20 2.6v4.8M17.6 5h4.8" stroke={CANVAS} strokeWidth={2.4} strokeLinecap="round" />
      </g>
      <g transform={`translate(4 18) scale(${s.smallScaleB}) translate(-4 -18)`}>
        <path d="M4 16.2v3.6M2.2 18h3.6" stroke={CANVAS} strokeWidth={2.4} strokeLinecap="round" />
      </g>
    </svg>
  );
}

function ReframeIcon({ p }: { p: number }) {
  const lerp = (a: number, b: number) => a + (b - a) * p;
  const playScale = lerp(1, 0.8);
  return (
    <svg {...ICON} aria-hidden>
      <rect x={lerp(1.5, 6)} y={lerp(6, 1.5)} width={lerp(21, 12)} height={lerp(12, 21)} rx={2.4} fill="none" stroke={CANVAS} strokeWidth={2.6} />
      <path d="M10.2 9.4v5.2l4.4-2.6z" fill={CANVAS} transform={`translate(12.4 12) scale(${playScale}) translate(-12.4 -12)`} />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg {...ICON} aria-hidden>
      <path d="M3 12h16M13.5 6.5 19 12l-5.5 5.5" fill="none" stroke={CANVAS} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ScissorsIcon({ angle }: { angle: number }) {
  return (
    <svg {...ICON} aria-hidden>
      <g transform={`rotate(${angle} 12 12)`}>
        <circle cx={6} cy={6} r={3} fill="none" stroke={CANVAS} strokeWidth={2.6} />
        <path d="M8.12 8.12 20 20" stroke={CANVAS} strokeWidth={2.6} strokeLinecap="round" />
      </g>
      <g transform={`rotate(${-angle} 12 12)`}>
        <circle cx={6} cy={18} r={3} fill="none" stroke={CANVAS} strokeWidth={2.6} />
        <path d="M20 4 8.12 15.88" stroke={CANVAS} strokeWidth={2.6} strokeLinecap="round" />
      </g>
    </svg>
  );
}

function Line({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: '50%',
        height: '100%',
        width: 'max-content',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        whiteSpace: 'nowrap',
        color: CANVAS,
        fontSize: 50,
        fontWeight: 700,
        letterSpacing: '-0.025em',
        transform: 'translateX(-50%)',
      }}
    >
      {children}
    </div>
  );
}

/** The 900×300 watermark artwork for one animation state. */
export function WatermarkArt({ state }: { state: WatermarkFrameState }) {
  const [a, b, c] = state.lineItemY;
  const mask = {
    WebkitMaskImage: `url(${AUTOCLIPPER_LOGO_DATA_URL})`,
    maskImage: `url(${AUTOCLIPPER_LOGO_DATA_URL})`,
    WebkitMaskSize: '100% 100%',
    maskSize: '100% 100%',
  } as React.CSSProperties;
  const rect = { ...TRACE_RECT, fill: 'none', strokeLinecap: 'round' as const, strokeDasharray: TRACE_PERIMETER, strokeDashoffset: TRACE_PERIMETER * state.trace.dashOffset };
  return (
    <div style={{ position: 'relative', width: WATERMARK_DESIGN_WIDTH, height: WATERMARK_DESIGN_HEIGHT, fontFamily: `${WATERMARK_FONT_FAMILY}, sans-serif` }}>
      {/* Wordmark: feathered scrim, dark box fill, logo, shine, trace. */}
      <div style={{ position: 'absolute', left: 64, top: 126, width: 772, height: 142, borderRadius: 64, background: 'rgba(8, 13, 5, 0.5)', filter: 'blur(14px)' }} />
      <div style={{ position: 'absolute', left: 363, top: 118, width: 452, height: 157, borderRadius: 19, background: 'linear-gradient(180deg, rgba(25, 31, 22, 0.86), rgba(8, 13, 5, 0.86))' }} />
      <div style={{ position: 'absolute', left: 80, top: 114, width: 740, height: 165.45 }}>
        <Img src={AUTOCLIPPER_LOGO_DATA_URL} alt="" style={{ display: 'block', width: '100%', height: '100%' }} />
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...mask }}>
          <div
            style={{
              position: 'absolute',
              top: '-20%',
              left: 0,
              width: '26%',
              height: '140%',
              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0) 100%)',
              transform: `translateX(${state.shineXPercent}%)`,
            }}
          />
        </div>
      </div>
      <svg width={WATERMARK_DESIGN_WIDTH} height={WATERMARK_DESIGN_HEIGHT} viewBox={`0 0 ${WATERMARK_DESIGN_WIDTH} ${WATERMARK_DESIGN_HEIGHT}`} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', opacity: state.trace.opacity }} aria-hidden>
        <rect {...rect} stroke={LIME_BRIGHT} strokeWidth={9} style={{ filter: 'blur(5px)' }} />
        <rect {...rect} stroke={INK} strokeWidth={5} />
      </svg>

      {/* Pill with the rotating tagline. */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 18, height: 84, display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', height: '100%', width: state.pillWidth }}>
          <div style={{ position: 'absolute', inset: -2, borderRadius: 999, background: LIME, filter: 'blur(10px)', opacity: state.haloOpacity }} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 999,
              overflow: 'hidden',
              background: `linear-gradient(90deg, ${LIME_BRIGHT}, ${LIME})`,
              boxShadow: 'inset 0 0 0 2px rgba(8, 13, 5, 0.18), 0 6px 14px rgba(0, 0, 0, 0.35)',
            }}
          >
            <Line>
              <Item y={a[0]}><SparklesIcon s={state.sparkle} /></Item>
              <Item y={a[1]}>Feito</Item>
              <Item y={a[2]}>por</Item>
              <Item y={a[3]}><Chip text="IA" /></Item>
              <Item y={a[4]}>com</Item>
            </Line>
            <Line>
              <Item y={b[0]}><ReframeIcon p={state.reframeProgress} /></Item>
              <Item y={b[1]}>Vídeo</Item>
              <Item y={b[2]}>longo</Item>
              <Item y={b[3]}><ArrowIcon /></Item>
              <Item y={b[4]}><Chip text="cortes" /></Item>
            </Line>
            <Line>
              <Item y={c[0]}><ScissorsIcon angle={state.scissorsAngle} /></Item>
              <Item y={c[1]}>Crie</Item>
              <Item y={c[2]}>o</Item>
              <Item y={c[3]}>seu</Item>
              <Item y={c[4]}><Chip text="grátis" scale={state.chipScale} /></Item>
            </Line>
          </div>
        </div>
      </div>
    </div>
  );
}
